/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Users & seats, as the screens show it: filtering the directory
 * (GET /users/directory), the seat counters in the header, who a bulk seat
 * change would reach, and the API's error codes in words.
 */

/** Short names for seat add-ons in column headers and counters. */
export const SEAT_SHORT = { CALL_CENTER: "Calling", WHATSAPP: "WhatsApp", AI_ASSISTANT: "AI Assistant" };
export const seatName = (m) => SEAT_SHORT[m.module_code] || m.label || m.module_code;

export const fullName = (u) => u.full_name || [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username || u.email || "Unnamed";

export const initials = (u) =>
  fullName(u)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

/** "Sales rep +2" when a person also holds add-on roles. */
export function roleSummary(u) {
  const base = u.base_role?.name || "No role";
  const extra = (u.addon_roles || []).length;
  return extra ? `${base} +${extra}` : base;
}

export const STATUS = {
  active: { label: "Active", tone: "success" },
  invited: { label: "Invited", tone: "warning" },
  inactive: { label: "Inactive", tone: "neutral" },
};

/**
 * filters: { query, roleId, teamId, seat: "CALL_CENTER:yes" | "CALL_CENTER:no" | "", status: "all"|"active"|"invited"|"inactive" }
 */
export function filterUsers(users, filters = {}) {
  const q = String(filters.query || "").trim().toLowerCase();
  const [seatCode, seatWant] = String(filters.seat || "").split(":");
  return users.filter((u) => {
    if (filters.status && filters.status !== "all" && u.status !== filters.status) return false;
    if (filters.roleId && u.base_role?.id !== filters.roleId && !(u.addon_roles || []).some((r) => r.id === filters.roleId)) return false;
    if (filters.teamId && !(u.teams || []).some((t) => t.id === filters.teamId)) return false;
    if (seatCode && Boolean(u.seats?.[seatCode]) !== (seatWant === "yes")) return false;
    if (q) {
      const hay = `${fullName(u)} ${u.email || ""} ${u.username || ""} ${u.designation || ""} ${u.department || ""}`.toLowerCase();
      if (!q.split(/\s+/).every((w) => hay.includes(w))) return false;
    }
    return true;
  });
}

/** "6 of 9 Calling · 2 of 4 WhatsApp". */
export function seatCounters(seatModules = []) {
  return seatModules.map((m) => `${m.used_seats} of ${m.total_seats} ${seatName(m)}`).join(" · ");
}

/** Who an "assign" would add and whether there are enough seats. */
export function planSeatAssign(users, ids, module) {
  const chosen = users.filter((u) => ids.includes(u.id));
  const toAssign = chosen.filter((u) => !u.seats?.[module.module_code]);
  const ineligible = toAssign.filter((u) => u.status === "inactive" || u.is_support_user);
  const eligible = toAssign.filter((u) => !ineligible.includes(u));
  const shortfall = Math.max(0, eligible.length - (module.seats_remaining ?? 0));
  return { toAssign: eligible, already: chosen.length - toAssign.length, ineligible, shortfall };
}

const CODE_MESSAGES = {
  CAPABILITY_REQUIRED: "You don't have permission to change that.",
  ADMINISTRATOR_PROTECTED: "Only an administrator can change another administrator's email or status.",
  SELF_STATUS_CHANGE: "You can't deactivate yourself.",
  EMAIL_TAKEN: "Another user already has that email.",
  LAST_ADMINISTRATOR: "This is the last administrator; give someone else the administrator role first.",
  BASE_ROLE_REQUIRED: "Everyone needs a base role.",
  MULTIPLE_PRIMARY_TEAMS: "Only one team can be the primary team.",
  LICENSE_LIMIT_REACHED: "No seats left for that add-on.",
  SEAT_SHORTFALL: "Not enough seats for everyone selected.",
  INVALID_SEAT_USERS: "Some selected people can't have a seat (deactivated, support or integration users).",
  ADDON_NOT_PURCHASED: "That add-on isn't on your plan.",
};

/** The API's error ({detail: string | {message, error_code}, part}) in words. */
export function apiErrorText(error, fallback = "Something went wrong.") {
  const data = error?.response?.data;
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (detail?.error_code && CODE_MESSAGES[detail.error_code]) return CODE_MESSAGES[detail.error_code];
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join("; ");
  return error?.message || fallback;
}

/** The PUT /users/{id}/access body: only what changed. */
export function accessChanges(original, draft) {
  const body = {};
  ["first_name", "last_name", "email", "designation", "department", "manager_id", "is_active"].forEach((k) => {
    if ((draft[k] ?? null) !== (original[k] ?? null)) body[k] = draft[k] ?? null;
  });
  if ((draft.base_role?.id ?? null) !== (original.base_role?.id ?? null)) body.base_role_id = draft.base_role?.id ?? null;
  const ids = (rs) => (rs || []).map((r) => r.id).sort((a, b) => a - b).join(",");
  if (ids(draft.addon_roles) !== ids(original.addon_roles)) body.addon_role_ids = (draft.addon_roles || []).map((r) => r.id);
  const teamKey = (ts) => (ts || []).map((t) => `${t.id}:${t.membership}:${t.is_primary ? 1 : 0}`).sort().join(",");
  if (teamKey(draft.teams) !== teamKey(original.teams)) {
    body.teams = (draft.teams || []).map((t) => ({ team_id: t.id, membership: t.membership || "member", is_primary: Boolean(t.is_primary) }));
  }
  const seatChanges = Object.keys(draft.seats || {}).filter((m) => Boolean(draft.seats[m]) !== Boolean(original.seats?.[m]));
  if (seatChanges.length) body.seats = Object.fromEntries(seatChanges.map((m) => [m, Boolean(draft.seats[m])]));
  return body;
}
